// src/pages/buyer/SamplesPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Package, Clock, CheckCircle, Truck, Star, ShoppingCart, ChevronDown, ChevronUp, MapPin } from 'lucide-react'
import { samplesApi } from '../../api/services'
import { Button, StatusBadge, PageSpinner, Empty, Card, CardHeader, CardBody } from '../../components/common/ui'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'

const STATUS_TABS = [
  { key: 'all',                label: 'All' },
  { key: 'pending',            label: 'Pending' },
  { key: 'approved',           label: 'Approved' },
  { key: 'dispatched',         label: 'Dispatched' },
  { key: 'delivered',          label: 'Delivered' },
  { key: 'feedback_submitted', label: 'Reviewed' },
]

const PIPELINE_STEPS = [
  { key: 'pending',    label: 'Requested',  icon: Clock },
  { key: 'approved',   label: 'Approved',   icon: CheckCircle },
  { key: 'dispatched', label: 'Dispatched', icon: Truck },
  { key: 'delivered',  label: 'Delivered',  icon: Package },
  { key: 'feedback_submitted', label: 'Reviewed', icon: Star },
]

const STATUS_ORDER = ['pending', 'approved', 'dispatched', 'delivered', 'feedback_submitted']

export default function SamplesPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('all')
  const [expanded, setExpanded] = useState(null)
  const [feedbackForm, setFeedbackForm] = useState({})
  const [showFeedback, setShowFeedback] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['buyer-samples', tab],
    queryFn: () => samplesApi.list(tab !== 'all' ? { status: tab } : {}),
  })

  const markDelivered = useMutation({
    mutationFn: (id) => samplesApi.markDelivered(id),
    onSuccess: () => { toast.success('Marked as delivered!'); qc.invalidateQueries({ queryKey: ['buyer-samples'] }) },
    onError: e => toast.error(e.response?.data?.message || 'Failed'),
  })

  const submitFeedback = useMutation({
    mutationFn: ({ id, ...d }) => samplesApi.submitFeedback(id, d),
    onSuccess: () => { toast.success('Feedback submitted!'); qc.invalidateQueries({ queryKey: ['buyer-samples'] }); setShowFeedback(null); setFeedbackForm({}) },
    onError: e => toast.error(e.response?.data?.message || 'Failed'),
  })

  if (isLoading) return <PageSpinner />

  const payload = data?.data?.data || {}
  const requests = payload?.requests?.data || payload?.data || []
  const counts = payload?.counts || {}

  const getStepIndex = (status) => STATUS_ORDER.indexOf(status)

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="text-xl font-display font-bold flex items-center gap-2">
          <Package size={20} className="text-pink" /> Sample Requests
        </h1>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {STATUS_TABS.filter(t => t.key !== 'all').map(t => (
          <div key={t.key} className="bg-white border border-border rounded-xl p-3 text-center">
            <p className="text-2xl font-extrabold text-gray-900">{counts[t.key] || 0}</p>
            <p className="text-[11px] text-muted font-bold uppercase tracking-wider">{t.label}</p>
          </div>
        ))}
      </div>

      {/* Status Tabs */}
      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1">
        {STATUS_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
              tab === t.key ? 'bg-pink text-white' : 'bg-surface text-muted hover:bg-gray-100'
            }`}
          >
            {t.label}
            {t.key !== 'all' && counts[t.key] > 0 && (
              <span className="ml-1 text-[10px]">({counts[t.key]})</span>
            )}
          </button>
        ))}
      </div>

      {/* Empty */}
      {requests.length === 0 && (
        <Empty
          icon="📦"
          title="No sample requests"
          description="Request a fabric sample from any product page."
          action={<Link to="/fabrics"><Button>Browse Fabrics</Button></Link>}
        />
      )}

      {/* Sample Cards */}
      <div className="space-y-4">
        {requests.map(sr => {
          const isExpanded = expanded === sr.id
          const stepIdx = getStepIndex(sr.status)
          const isRejected = sr.status === 'rejected'

          return (
            <Card key={sr.id} className={isRejected ? 'border-red-200 bg-red-50/30' : ''}>
              <CardBody>
                {/* Main Row */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-surface border border-border overflow-hidden flex-shrink-0">
                    {sr.product?.primary_image_url && (
                      <img src={sr.product.primary_image_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-bold text-sm text-gray-900 line-clamp-1">{sr.product?.name}</span>
                      <StatusBadge status={sr.status} />
                    </div>
                    <p className="text-xs text-muted">
                      {sr.request_number} · {sr.supplier?.business_name || sr.supplier?.name}
                    </p>
                    {sr.variant?.color_name && (
                      <p className="text-xs text-muted">Variant: {sr.variant.color_name}</p>
                    )}
                    {sr.purpose && <p className="text-xs text-gray-600 mt-0.5">Purpose: {sr.purpose}</p>}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted">{new Date(sr.created_at).toLocaleDateString('en-IN')}</span>
                    <button onClick={() => setExpanded(isExpanded ? null : sr.id)} className="text-muted hover:text-gray-900">
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {/* Tracking Info */}
                {sr.tracking_number && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">
                    <Truck size={14} />
                    <span>{sr.courier_name} — <span className="font-mono font-bold">{sr.tracking_number}</span></span>
                  </div>
                )}

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-border">
                    {/* Visual Pipeline */}
                    {!isRejected && (
                      <div className="flex items-center justify-between mb-4">
                        {PIPELINE_STEPS.map((step, i) => {
                          const isComplete = i < stepIdx || sr.status === 'feedback_submitted'
                          const isCurrent = i === stepIdx && sr.status !== 'feedback_submitted'
                          const Icon = step.icon
                          return (
                            <div key={step.key} className="flex items-center flex-1 last:flex-initial">
                              <div className="flex flex-col items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                                  isComplete ? 'bg-green-500 border-green-500 text-white'
                                  : isCurrent ? 'border-pink bg-pink/10 text-pink'
                                  : 'border-gray-200 bg-gray-50 text-gray-300'
                                }`}>
                                  {isComplete ? <CheckCircle size={14} /> : <Icon size={14} />}
                                </div>
                                <span className={`text-[10px] font-bold mt-1 ${
                                  isComplete ? 'text-green-600' : isCurrent ? 'text-gray-900' : 'text-gray-300'
                                }`}>{step.label}</span>
                              </div>
                              {i < PIPELINE_STEPS.length - 1 && (
                                <div className={`flex-1 h-0.5 mx-2 mt-[-14px] ${i < stepIdx || sr.status === 'feedback_submitted' ? 'bg-green-500' : 'bg-gray-200'}`} />
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* Rejected Reason */}
                    {isRejected && sr.rejection_reason && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                        <p className="text-sm font-bold text-red-800">Rejected</p>
                        <p className="text-xs text-red-600 mt-0.5">{sr.rejection_reason}</p>
                      </div>
                    )}

                    {/* Shipping Address */}
                    <div className="flex items-start gap-2 text-xs text-gray-600 mb-3">
                      <MapPin size={14} className="mt-0.5 text-muted flex-shrink-0" />
                      <span>{sr.shipping_name}, {sr.shipping_address}, {sr.shipping_city}, {sr.shipping_state} - {sr.shipping_pincode}</span>
                    </div>

                    {/* Supplier Notes */}
                    {sr.supplier_notes && (
                      <p className="text-xs text-gray-600 mb-3">
                        <span className="font-bold">Supplier note:</span> {sr.supplier_notes}
                      </p>
                    )}

                    {/* Sample Price */}
                    {sr.sample_price > 0 && (
                      <p className="text-xs text-gray-600 mb-3">
                        Sample price: <span className="font-bold">₹{Number(sr.sample_price).toLocaleString('en-IN')}</span>
                      </p>
                    )}

                    {/* Feedback Display */}
                    {sr.feedback_rating && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                        <div className="flex items-center gap-1 mb-1">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} size={14} className={i <= sr.feedback_rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'} />
                          ))}
                          <span className="text-xs font-bold text-amber-700 ml-1">{sr.feedback_rating}/5</span>
                        </div>
                        {sr.feedback_notes && <p className="text-xs text-amber-700">{sr.feedback_notes}</p>}
                        {sr.will_place_bulk_order && <p className="text-xs text-green-700 font-bold mt-1">Interested in bulk order</p>}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap">
                      {/* Mark Delivered */}
                      {sr.status === 'dispatched' && (
                        <Button size="sm" loading={markDelivered.isPending} onClick={() => markDelivered.mutate(sr.id)}>
                          <CheckCircle size={14} /> Mark Delivered
                        </Button>
                      )}

                      {/* Submit Feedback */}
                      {sr.status === 'delivered' && !sr.feedback_rating && (
                        <Button size="sm" variant="secondary" onClick={() => setShowFeedback(sr.id)}>
                          <Star size={14} /> Rate Sample
                        </Button>
                      )}

                      {/* Convert to Order */}
                      {['delivered', 'feedback_submitted'].includes(sr.status) && (
                        <Link to={`/fabrics/${sr.product?.slug}`}>
                          <Button size="sm" variant="secondary">
                            <ShoppingCart size={14} /> Place Bulk Order
                          </Button>
                        </Link>
                      )}
                    </div>

                    {/* Feedback Form */}
                    {showFeedback === sr.id && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-sm font-bold mb-2">Rate this sample</p>

                        {/* Star Rating */}
                        <div className="flex gap-1 mb-3">
                          {[1,2,3,4,5].map(i => (
                            <button
                              key={i}
                              onClick={() => setFeedbackForm(f => ({ ...f, rating: i }))}
                              className="focus:outline-none"
                            >
                              <Star
                                size={24}
                                className={`transition-colors ${i <= (feedbackForm.rating || 0) ? 'text-amber-500 fill-amber-500' : 'text-gray-300 hover:text-amber-300'}`}
                              />
                            </button>
                          ))}
                        </div>

                        <textarea
                          className="input w-full h-16 mb-2"
                          placeholder="How was the fabric quality, color, texture...?"
                          value={feedbackForm.notes || ''}
                          onChange={e => setFeedbackForm(f => ({ ...f, notes: e.target.value }))}
                        />

                        <label className="flex items-center gap-2 text-xs text-gray-700 mb-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={feedbackForm.will_place_bulk_order || false}
                            onChange={e => setFeedbackForm(f => ({ ...f, will_place_bulk_order: e.target.checked }))}
                            className="rounded border-gray-300"
                          />
                          I'm interested in placing a bulk order for this fabric
                        </label>

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            loading={submitFeedback.isPending}
                            disabled={!feedbackForm.rating}
                            onClick={() => submitFeedback.mutate({ id: sr.id, ...feedbackForm })}
                          >
                            Submit Feedback
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setShowFeedback(null); setFeedbackForm({}) }}>Cancel</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
